export const getRedirectUri = () => {
  const isCampus = localStorage.getItem('campusToken');

  if (isCampus) {
    return process.env.REACT_APP_REDIRECT_URI_CAMPUS || 'https://campusvirtual.utdt.edu';
  }

  return process.env.REACT_APP_REDIRECT_URI || 'https://campusvirtual.utdt.edu';
};

/** Código de error del backend cuando el IPO del alumno no está en la whitelist. */
export const IPO_NOT_IN_WHITELIST = "IPO_NOT_IN_WHITELIST";

export const IPO_NOT_IN_WHITELIST_DEFAULT_MSG =
  "Tu carrera no está habilitada para el sistema de inscripciones.";

/** Body tipado de errores 401 específicos de auth/whitelist. */
export type AuthErrorBody = {
  code?: string;
  error?: string;
};

/** Extrae el body de un AxiosError (o error envuelto en login/campus/elegirCarrera). */
export function getAuthErrorBody(error: unknown): AuthErrorBody | null {
  if (!error || typeof error !== "object") return null;
  const axiosData = (error as { response?: { data?: AuthErrorBody } }).response?.data;
  if (axiosData && typeof axiosData === "object") return axiosData;
  const direct = error as AuthErrorBody;
  if (direct.code) return direct;
  return null;
}

export function isIpoNotInWhitelistError(error: unknown): boolean {
  return getAuthErrorBody(error)?.code === IPO_NOT_IN_WHITELIST;
}

export function getIpoNotInWhitelistMessage(error: unknown): string {
  return getAuthErrorBody(error)?.error || IPO_NOT_IN_WHITELIST_DEFAULT_MSG;
}

/**
 * 401/404 esperados del flujo de login no deben marcarse como serverError genérico
 * (evita el toast "Surgió un error" duplicado).
 */
export function isExpectedLoginAuthError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 404;
}

/** Endpoints de login donde un 401 no implica sesión inválida / logout. */
export function isLoginEndpointUrl(url?: string): boolean {
  if (!url) return false;
  return url.includes("doLogin") || url.includes("doCampusLogin");
}

/** True si el 401 no debe forzar logout (login endpoints o IPO fuera de whitelist). */
export function shouldSkipUnauthorizedLogout(error: unknown, url?: string): boolean {
  return isIpoNotInWhitelistError(error) || isLoginEndpointUrl(url);
}



export function getTokenExp(token: string) {
  try {
    const payloadBase64 = token.split('.')[1];
    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const payloadJson = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );

    const payload = JSON.parse(payloadJson);
    return payload.exp ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

let autoLogoutTimeoutId: ReturnType<typeof setTimeout> | null = null;
let logoutCallback: (() => void | Promise<void>) | null = null;
function triggerAutoLogout(reason: string) {
  console.log(`🚪 [autoLogout] Ejecutando logout: ${reason}`);
  if (logoutCallback) {
    void Promise.resolve(logoutCallback());
  } else {
    console.warn("⚠️ [autoLogout] No hay callback de logout registrado");
  }
}
export function isTokenExpired(token: string): boolean {
  const exp = getTokenExp(token);
  if (!exp) return false;
  return Date.now() >= exp;
}
export function validateTokenExpirationOnFocus(): boolean {
  const token = localStorage.getItem("token");
  if (!token) return false;
  if (isTokenExpired(token)) {
    const exp = getTokenExp(token);
    console.log(
      `🕐 [autoLogout] Token vencido al volver al foco (exp: ${exp ? new Date(exp).toLocaleString() : "desconocido"})`,
    );

    triggerAutoLogout("token vencido al volver al foco");
    return true;
  }

  return false;
}



export function setupVisibilityTokenCheck() {
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      validateTokenExpirationOnFocus();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };

}
export function registerLogoutCallback(callback: () => void | Promise<void>) {
  logoutCallback = callback;
}
export function clearAutoLogout() {
  if (autoLogoutTimeoutId !== null) {
    clearTimeout(autoLogoutTimeoutId);
    autoLogoutTimeoutId = null;
  }
}

export function scheduleAutoLogout(token: string) {
  clearAutoLogout();
  const exp = getTokenExp(token);

  if (!exp) return;
  const delay = exp - Date.now();
  const logoutAt = new Date(exp).toLocaleString();
  if (delay <= 0) {
    console.log(`🕐 [autoLogout] Token ya vencido (exp: ${logoutAt}), logout inmediato`);
    triggerAutoLogout("token ya vencido al programar");
    return;
  }

  const delayMinutes = Math.round(delay / 60000);

  console.log(
    `🕐 [autoLogout] Logout programado para ${logoutAt} (en ~${delayMinutes} min, ${Math.round(delay / 1000)}s)`,
  );

  autoLogoutTimeoutId = setTimeout(
    () => triggerAutoLogout("vencimiento programado del token"),
    delay,
  );
}

